/**
 * Filename Generation Utilities for QR Code Downloads
 *
 * This module provides pure functions for generating sanitized filenames
 * for single QR code downloads and batch ZIP downloads.
 *
 * Requirements: 7.3, 8.5
 */

import type { PreviewTarget } from '../types';

// Re-export PreviewTarget for backwards compatibility
export type { PreviewTarget } from '../types';

/**
 * Characters that are invalid in filenames across common operating systems
 * Includes: < > : " / \ | ? * and control characters
 */
const INVALID_FILENAME_CHARS = /[<>:"/\\|?*\x00-\x1F]/g;

/**
 * Characters that should be replaced with underscores for cleaner filenames
 * Includes spaces and other potentially problematic characters
 */
const REPLACEABLE_CHARS = /[\s.]+/g;

/**
 * Sanitizes a string to be safe for use as a filename
 *
 * @param name - The raw string to sanitize
 * @returns A sanitized string safe for use in filenames
 *
 * Sanitization rules:
 * - Removes invalid filename characters (< > : " / \ | ? *)
 * - Replaces spaces and dots with underscores
 * - Collapses multiple consecutive underscores into one
 * - Trims leading/trailing underscores
 * - Returns 'unnamed' if result would be empty
 */
export function sanitizeFilename(name: string): string {
  if (!name || typeof name !== 'string') {
    return 'unnamed';
  }

  const sanitized = name
    // Remove invalid characters
    .replace(INVALID_FILENAME_CHARS, '')
    // Replace spaces and dots with underscores
    .replace(REPLACEABLE_CHARS, '_')
    // Collapse multiple underscores into one
    .replace(/_+/g, '_')
    // Trim leading/trailing underscores
    .replace(/^_+|_+$/g, '');

  return sanitized || 'unnamed';
}

/**
 * Generates the download filename for a single QR code
 *
 * @param siteName - The name of the site
 * @param target - The preview target (site, fixed-bay, or super-bay)
 * @returns The formatted filename following pattern: {sanitizedSiteName}_{bayId|site}_qr.png
 *
 * Requirement 7.3: THE downloaded file SHALL be named using the pattern
 * `{siteName}_{bayId|site}_qr.png`
 *
 * Property 8: Single Download Filename Pattern - {sanitizedSiteName}_{bayId|site}_qr.png
 * where siteName is sanitized to remove invalid filename characters
 */
export function generateDownloadFilename(
  siteName: string,
  target: PreviewTarget
): string {
  const sanitizedSiteName = sanitizeFilename(siteName);

  switch (target.type) {
    case 'site':
      return `${sanitizedSiteName}_site_qr.png`;
    case 'fixed-bay':
      return `${sanitizedSiteName}_${sanitizeFilename(target.bayId)}_qr.png`;
    case 'super-bay':
      return `${sanitizedSiteName}_${sanitizeFilename(target.bayId)}_qr.png`;
    default: {
      // Exhaustive check - TypeScript will error if a case is missed
      const _exhaustiveCheck: never = target;
      return _exhaustiveCheck;
    }
  }
}

/**
 * Generates the filename for a batch ZIP download containing all bay QR codes
 *
 * @param siteName - The name of the site
 * @returns The formatted ZIP filename following pattern: {sanitizedSiteName}_custom_qr_codes.zip
 *
 * Requirement 8.5: THE ZIP file SHALL be named using the pattern
 * `{siteName}_custom_qr_codes.zip`
 *
 * Property 10: Batch ZIP Filename Pattern - {sanitizedSiteName}_custom_qr_codes.zip
 */
export function generateZipFilename(siteName: string): string {
  const sanitizedSiteName = sanitizeFilename(siteName);
  return `${sanitizedSiteName}_custom_qr_codes.zip`;
}
